import type { DataSession } from "../../../../data/core/session";
import { Platform } from "react-native";
import { addAudioBreadcrumb } from "../../audio/audioSentry";
import {
  ON_DEMAND_POLL_INTERVAL_MS,
  ON_DEMAND_POLL_TIMEOUT_MS,
} from "./constants";

// Master playlists (what an internal song's `urlOnline` points at) declare
// renditions with #EXT-X-STREAM-INF, not #EXTINF (that's the per-segment tag
// inside a media playlist). The worker's upload ordering guarantees the 64k
// media playlist + first segment exist before the master is written, so this
// single check on the master is sufficient as the "playable" gate.
const MASTER_PLAYLIST_MARKER = "#EXT-X-STREAM-INF";

// Two-master publish: `songs/<id>/master.tmp.m3u8` is the gen-0 master,
// written the moment the song is playable and pointing at the growing
// `.tmp.` rendition playlists (no-cache, swept by the 1-day `.tmp.`
// lifecycle rule). `songs/<id>/master.m3u8` is the FINAL master, written
// ONCE at finalization, pointing only at immutable renditions — its mere
// existence means the song is fully published, so a 404 on it unambiguously
// means "not finalized" (never a false negative from a lifecycle sweep).
// `.tmp.` is the ONLY structural difference between the two URLs (see
// `contexts/audio-publish/contracts/src/mediaUrls.ts`'s `onDemandAudioMasterObjectPath` /
// `onDemandAudioMasterTmpObjectPath`, the server-side source of truth this
// mirrors).
const FINAL_MASTER_FILENAME = "master.m3u8";
const TMP_MASTER_FILENAME = "master.tmp.m3u8";

const delay = (ms: number) =>
  new Promise<void>((resolve) => setTimeout(resolve, ms));

/** Whether `url`'s PATH is the tmp (gen-0) master, not the final one. */
function isTmpMasterUrl(url: string): boolean {
  return new URL(url).pathname.endsWith(`/${TMP_MASTER_FILENAME}`);
}

/**
 * Only the on-demand publish masters participate in the tmp/final state
 * machine. Public preview media playlists (for example
 * `preview/preview.m3u8`) are already directly playable and must never be
 * rewritten to a non-existent `.tmp` sibling.
 */
export function isOnDemandMasterUrl(url: string): boolean {
  const pathname = new URL(url).pathname;
  return (
    pathname.endsWith(`/${FINAL_MASTER_FILENAME}`) ||
    pathname.endsWith(`/${TMP_MASTER_FILENAME}`)
  );
}

/**
 * `master.m3u8` -> `master.tmp.m3u8`. Operates on the URL PATH only —
 * preserves any query string untouched (there normally isn't one: this is
 * called with the unsigned `urlOnline`, never an already-signed URL, so the
 * signature is always computed fresh for the resulting path).
 */
function toTmpMasterUrl(url: string): string {
  const parsed = new URL(url);
  parsed.pathname = parsed.pathname.replace(
    /\/master\.m3u8$/,
    `/${TMP_MASTER_FILENAME}`,
  );
  return parsed.toString();
}

/** `master.tmp.m3u8` -> `master.m3u8`. Inverse of `toTmpMasterUrl`. */
function toFinalMasterUrl(url: string): string {
  const parsed = new URL(url);
  parsed.pathname = parsed.pathname.replace(
    /\/master\.tmp\.m3u8$/,
    `/${FINAL_MASTER_FILENAME}`,
  );
  return parsed.toString();
}

async function checkPlaylistReady(
  fetchImpl: typeof fetch,
  url: string,
): Promise<"ready" | "not-found" | "unavailable"> {
  try {
    // Cache-bypass strategy is platform-split ON PURPOSE (prod incident
    // 2026-07-15): custom request headers make a browser fetch
    // "non-simple", so EVERY poll tick preflights (OPTIONS + GET), and the
    // CDN's preflight response didn't allow Cache-Control at all —
    // playback on web was fully CORS-blocked. On web we rely on
    // `cache: "no-store"` (a browser-cache directive, adds no header, so
    // a bare GET stays a simple request with no preflight). React Native's
    // fetch ignores the `cache` option, so native keeps the explicit
    // headers (no CORS on native).
    const response = await fetchImpl(
      url,
      Platform.OS === "web"
        ? { cache: "no-store" }
        : { headers: { "Cache-Control": "no-cache", Pragma: "no-cache" } },
    );
    // Only a real 404 proves the publish object is absent. Authentication
    // failures, server errors, and malformed responses are inconclusive and
    // must never be used as evidence to request a republish.
    if (response.status === 404) return "not-found";
    if (response.status !== 200) return "unavailable";
    const body = await response.text();
    return body.includes(MASTER_PLAYLIST_MARKER) ? "ready" : "unavailable";
  } catch (error) {
    // A connectivity failure says nothing about whether the CDN object
    // exists. Keep polling the same candidate within the overall timeout.
    console.audio.warn("onDemandPublish/pollForPlayableAudio: check failed", {
      url,
      error: (error as Error).message,
    });
    return "unavailable";
  }
}

/**
 * Publish intents can be lost (e.g. a Cloud Run surge-abort) — as a
 * self-rescue, if the very first poll tick comes back a miss, fire a
 * best-effort request to re-trigger publishing server-side. Fire-and-forget:
 * never awaited, all errors swallowed, never blocks or delays the poll loop,
 * and fires at most once per `pollForPlayableAudio` call regardless of how
 * many subsequent ticks miss.
 *
 * Kept as a separate helper so the poll state machine can fire it only after
 * observing a confirmed 404. Native playback errors and fetch exceptions do
 * not carry that evidence and must not call this helper.
 */
export function fireRescueRequest(
  songId: string,
  requestPublish: (songId: string) => Promise<unknown>,
): void {
  addAudioBreadcrumb(
    "ondemand_publish.rescue_requested",
    { songId },
    "warning",
  );
  // Wrap in Promise.resolve().then(...) so a synchronous throw from
  // `requestPublish` (e.g. a bad mock, or the real client misconfigured)
  // is caught the same way as an async rejection — never allowed to
  // propagate into the poll loop.
  Promise.resolve()
    .then(() => requestPublish(songId))
    .catch(() => {
      // Swallow all errors — this is a best-effort kick, not a required
      // step in the flow. The poll loop is the source of truth for
      // whether the song became playable.
    });
}

export type PollForPlayableAudioOptions = {
  intervalMs?: number;
  timeoutMs?: number;
  fetchImpl?: typeof fetch;
  requestPublish?: (songId: string) => Promise<unknown>;
};

export type PollForPlayableAudioResult =
  | {
      ready: true;
      /**
       * The URL that actually 200'd with a playable master — the tmp master
       * (`master.tmp.m3u8`) or the final one (`master.m3u8`), whichever the
       * state machine below landed on. NOT necessarily the `urlOnline` the
       * caller passed in: the poll may have crossed from one master to the
       * other (finalization landed mid-poll, or a stale-done zombie self-
       * healed). The caller MUST play this URL, not the original.
       */
      playableUrl: string;
      usedRescue: boolean;
    }
  | {
      ready: false;
      /**
       * True if at least one tick missed before the timeout was hit — i.e.
       * the CDN object was NOT already materialized on the very first
       * check. Callers use this to decide whether it's worth a follow-up
       * re-fetch of the song row (fresher metadata like duration may have
       * finalized alongside the audio) — skipping that extra round trip
       * for the overwhelming common case where the first tick already
       * succeeds.
       */
      usedRescue: boolean;
    };

/**
 * Polls the given song's `urlOnline` master playlist — which under the
 * two-master publish scheme is `master.tmp.m3u8` while a publish is in
 * flight, or `master.m3u8` once finalized or if the song never needed the
 * gen-0/tmp phase (unmaterialized/backfilled content) — implementing the
 * canonical client poll state machine:
 *
 * ```
 * loop until playable or timeout:
 *   if url is the tmp master:
 *     poll it: 200 -> PLAY on the tmp url (live gen-0)
 *     miss    -> poll the final master (url with ".tmp." stripped):
 *                  200 -> PLAY on the final url (it finalized mid-poll)
 *                  miss -> fire the one-shot rescue; stay on the tmp url;
 *                          continue loop
 *   else (final master):
 *     poll it: 200 -> PLAY on the final url
 *     miss    -> fire the one-shot rescue; switch to the tmp url;
 *                continue loop
 * ```
 *
 * "200" means HTTP 200 with a body containing at least one
 * #EXT-X-STREAM-INF rendition entry (see `checkPlaylistReady`). Because the
 * final master is written ONCE, at finalization, and its objects are
 * immutable/never swept, a 404 on it unambiguously means "not finalized (or
 * never started) — (re)publish", which is exactly what the tmp-url fallback
 * and the rescue address: the zombie case (finalization failed, tmp objects
 * lifecycle-swept) can no longer 200-while-broken, because the client always
 * ends up polling the LIVE tmp master, not a stale reference.
 *
 * Non-overlapping by construction: each loop iteration awaits its fetch(es)
 * in full before scheduling the next one after `intervalMs` — never stacks
 * concurrent requests.
 *
 * Each distinct candidate URL (tmp / final) is signed at most once and
 * cached for the life of this call: the CDN key is valid for hours, far
 * longer than the ~10s poll budget, so re-signing on every tick — or every
 * time the state machine crosses over — would be pure overhead.
 *
 * Self-rescue: fires a fire-and-forget `requestPublish` kick (see
 * `fireRescueRequest`) only after confirmed 404 responses: both candidate
 * URLs in the tmp branch, or the final candidate in the final branch. An
 * inconclusive network/HTTP failure never triggers rescue or changes the
 * current candidate.
 */
export async function pollForPlayableAudio(
  data: DataSession,
  songId: string,
  urlOnline: string,
  options?: PollForPlayableAudioOptions,
): Promise<PollForPlayableAudioResult> {
  const intervalMs = options?.intervalMs ?? ON_DEMAND_POLL_INTERVAL_MS;
  const timeoutMs = options?.timeoutMs ?? ON_DEMAND_POLL_TIMEOUT_MS;
  const fetchImpl = options?.fetchImpl ?? (globalThis.fetch as typeof fetch);
  const requestPublish =
    options?.requestPublish ??
    ((songId: string) =>
      data.api.catalog.songs.requestPublish.mutate({ songId }));

  // Lazily require to keep Jest/environment imports lightweight — mirrors
  // resolveSource.ts's existing lazy-require of the same signUrl module.
  // eslint-disable-next-line @typescript-eslint/no-require-imports
  const { signUrl } = require("@utils/index");

  const signedUrlCache = new Map<string, Promise<string>>();
  const getSignedUrl = (unsignedUrl: string): Promise<string> => {
    const existing = signedUrlCache.get(unsignedUrl);
    if (existing) return existing;
    const signed: Promise<string> = signUrl(data, unsignedUrl);
    signedUrlCache.set(unsignedUrl, signed);
    return signed;
  };

  const deadline = Date.now() + timeoutMs;
  let hasFiredRescue = false;
  // Any miss (on either candidate URL) means the object was not already
  // materialized on the very first check — see the doc comment on the
  // `usedRescue` field for why callers care.
  let hasMissed = false;
  let currentUrl = urlOnline;

  const fireRescueOnce = () => {
    if (hasFiredRescue) return;
    hasFiredRescue = true;
    fireRescueRequest(songId, requestPublish);
  };

  while (Date.now() < deadline) {
    if (isTmpMasterUrl(currentUrl)) {
      const signedTmp = await getSignedUrl(currentUrl);
      const tmpResult = await checkPlaylistReady(fetchImpl, signedTmp);
      if (tmpResult === "ready") {
        return { ready: true, playableUrl: currentUrl, usedRescue: hasMissed };
      }
      hasMissed = true;

      const finalUrl = toFinalMasterUrl(currentUrl);
      const signedFinal = await getSignedUrl(finalUrl);
      const finalResult = await checkPlaylistReady(fetchImpl, signedFinal);
      if (finalResult === "ready") {
        return { ready: true, playableUrl: finalUrl, usedRescue: hasMissed };
      }

      if (tmpResult === "not-found" && finalResult === "not-found") {
        // Both objects are confirmed absent. Fire the one-shot rescue and
        // stay on the tmp URL (the live-publish target) for the next tick.
        fireRescueOnce();
      }
    } else {
      const signedFinal = await getSignedUrl(currentUrl);
      const finalResult = await checkPlaylistReady(fetchImpl, signedFinal);
      if (finalResult === "ready") {
        return { ready: true, playableUrl: currentUrl, usedRescue: hasMissed };
      }
      hasMissed = true;
      if (finalResult === "not-found") {
        fireRescueOnce();
        // A confirmed final-master 404 means "not finalized (or never
        // started)" — cross over to the live-publish tmp target.
        currentUrl = toTmpMasterUrl(currentUrl);
      }
    }

    if (Date.now() >= deadline) return { ready: false, usedRescue: hasMissed };
    await delay(intervalMs);
  }
  return { ready: false, usedRescue: hasMissed };
}
